import React from 'react'
import { SpreadsheetReducerActions, ISpreadsheetData } from 'components/spreadsheet/types'
import { HEADER_HEIGHT_CLASS, COLUMN_MIN_WIDTH_CLASS } from 'style/constants'
import { Plus } from 'components/icons'
import Tooltip from 'components/tooltip'
import { useNewColumn } from 'components/spreadsheet/components/new-column/useNewColumn'
import NewColumnMenu from 'components/spreadsheet/components/new-column'

interface HeaderProps {
  spreadsheetData: ISpreadsheetData
  setSpreadsheetData: React.Dispatch<SpreadsheetReducerActions>
  totalWidth: number
}

// Where a column added at the end goes: after the last, or first in a view with none yet.
export const nextSortOrder = (columns: Array<{ sortOrder: number }>): number =>
  columns.reduce((max, column) => Math.max(max, column.sortOrder), -1) + 1

const HeaderAddColumn: React.FC<HeaderProps> = ({ spreadsheetData, setSpreadsheetData, totalWidth }) => {
  const newColumn = useNewColumn(spreadsheetData, setSpreadsheetData)

  // Clicking + opens Edit column for a new Text column, under where it will go. Nothing is created until
  // Add column.
  const handleAddColumn = (event: React.MouseEvent): void => {
    const rect = event.currentTarget.getBoundingClientRect()
    newColumn.open(nextSortOrder(spreadsheetData.viewDetails.columns), {
      top: `${rect.bottom}px`,
      left: `${rect.left}px`
    })
  }

  return (
    <div
      id={`header-column-add`}
      data-tour="add-column"
      className={`absolute box-border inline-block bg-surface-sunken z-auto cursor-default border-r border-r-divider ${HEADER_HEIGHT_CLASS} ${COLUMN_MIN_WIDTH_CLASS}`}
      style={{
        left: `${totalWidth}px`
      }}
      onClick={handleAddColumn}
    >
      {/* Secondary to the columns: a muted + that darkens on hover, the size
          of the header's other icons. */}
      <Tooltip content="New column">
        <div
          role="button"
          tabIndex={0}
          aria-label="New column"
          data-testid="header-add-column-button"
          className="w-full h-full flex items-center justify-center absolute text-muted cursor-pointer select-none hover:bg-surface-strong hover:text-body transition-colors [&>svg]:w-3.5 [&>svg]:h-3.5"
          onKeyDown={(event: React.KeyboardEvent<HTMLDivElement>) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault()
              event.currentTarget.click()
            }
          }}
        >
          <Plus />
        </div>
      </Tooltip>

      <NewColumnMenu id="add-column-menu" newColumn={newColumn} />
    </div>
  )
}

export default HeaderAddColumn
